// Drift Auditor -- per-holding drift & risk-contribution diagnostics (TanStack Table v8).

import React, { useMemo } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';

import type { HoldingDriftDiagnostic } from '../types';

const columnHelper = createColumnHelper<HoldingDriftDiagnostic>();

const columns = [
    columnHelper.accessor('ticker', {
        header: 'Ticker',
        cell: (c) => <span className="font-bold text-slate-800">{c.getValue()}</span>,
    }),
    columnHelper.accessor('target_weight_pct', {
        header: 'Target',
        cell: (c) => `${c.getValue().toFixed(1)}%`,
    }),
    columnHelper.accessor('final_drifted_weight_pct', {
        header: 'Drifted To',
        cell: (c) => `${c.getValue().toFixed(1)}%`,
    }),
    columnHelper.accessor('absolute_drift_pct', {
        header: 'Drift',
        cell: (c) => {
            const v = c.getValue();
            const cls = v > 0 ? 'text-emerald-600' : v < 0 ? 'text-rose-600' : 'text-slate-500';
            return (
                <span className={`font-semibold ${cls}`}>
                    {v > 0 ? '+' : ''}
                    {v.toFixed(1)}%
                </span>
            );
        },
    }),
    columnHelper.accessor('max_historical_weight_pct', {
        header: 'Peak Weight',
        cell: (c) => `${c.getValue().toFixed(1)}%`,
    }),
    columnHelper.accessor('risk_contribution_target_pct', {
        header: 'Risk @ Target',
        cell: (c) => `${c.getValue().toFixed(1)}%`,
    }),
    columnHelper.accessor('risk_contribution_drifted_pct', {
        header: 'Risk @ Drifted',
        cell: (c) => <span className="font-semibold text-slate-800">{`${c.getValue().toFixed(1)}%`}</span>,
    }),
];

export const HoldingDiagnosticsTable: React.FC<{ rows: HoldingDriftDiagnostic[] }> = ({ rows }) => {
    const data = useMemo(() => rows, [rows]);
    const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-5 pt-5 pb-3">
                <h3 className="text-base font-bold text-slate-800">Holding-Level Drift Diagnostics</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                    Where your weights — and your risk — ended up without rebalancing.
                </p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        {table.getHeaderGroups().map((hg) => (
                            <tr key={hg.id} className="bg-slate-50 border-y border-slate-100">
                                {hg.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap"
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="px-4 py-3 text-slate-700 whitespace-nowrap">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
